import { useState } from "react";
import { Link } from "react-router-dom";
import { resolveImageUrl } from "@/utils/imageUrl";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, FileText, Sparkles, Zap, Share2, Link2, X, Star } from "lucide-react";
import { toast } from "sonner";
import QuickBuyModal from "@/components/QuickBuyModal";

const SITE_URL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

function ShareMenu({ product, onClose }) {
  const shareUrl = `${SITE_URL}/mock-exams/${product.slug}`;
  const shareText = `Check out "${product.title}" on Great Messiah Publication!`;

  const copyLink = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied!");
    onClose();
  };

  const shareWhatsApp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, "_blank");
    onClose();
  };

  const shareTwitter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
    onClose();
  };

  const shareFacebook = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank");
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: -4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: -4 }}
      transition={{ duration: 0.15 }}
      className="absolute top-12 right-3 z-30 bg-white rounded-xl shadow-xl border border-gray-100 py-1 w-44"
      onClick={e => e.preventDefault()}
      data-testid={`share-menu-${product.slug}`}
    >
      <button onClick={copyLink} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-testid="share-copy-link">
        <Link2 className="w-4 h-4 text-gray-400" /> Copy Link
      </button>
      <button onClick={shareWhatsApp} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-testid="share-whatsapp">
        <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        WhatsApp
      </button>
      <button onClick={shareTwitter} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-testid="share-twitter">
        <svg className="w-4 h-4 text-gray-800" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        X (Twitter)
      </button>
      <button onClick={shareFacebook} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-testid="share-facebook">
        <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
        Facebook
      </button>
    </motion.div>
  );
}

export default function ProductCard({ product, rating }) {
  const { addItem, items } = useCart();
  const [quickBuyOpen, setQuickBuyOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const isInCart = items.some(item => item.product_id === product.id);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
    toast.success(`${product.title} added to cart`);
  };

  const handleQuickBuy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setQuickBuyOpen(true);
  };

  const toggleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShareOpen(prev => !prev);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS'
    }).format(price);
  };

  const discount = product.compare_at_price_ghs && product.compare_at_price_ghs > product.price_ghs
    ? Math.round((1 - product.price_ghs / product.compare_at_price_ghs) * 100)
    : null;

  return (
    <>
      <Link to={`/mock-exams/${product.slug}`} data-testid={`product-card-${product.slug}`}>
        <motion.div
          className="group bg-white rounded-2xl border border-gray-100 overflow-hidden h-full"
          whileHover={{ y: -8, boxShadow: "0 25px 50px rgba(0,0,0,0.12)" }}
          transition={{ duration: 0.3 }}
        >
          {/* Cover Image */}
          <div className="relative aspect-[4/5] bg-gray-100 overflow-hidden">
            {product.cover_image_url ? (
              <img
                src={resolveImageUrl(product.cover_image_url)}
                alt={product.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-navy-800 to-navy-900 text-white">
                <img src="/logo.png" alt="Great Messiah Publication" className="w-12 h-12 mb-3 opacity-50 object-contain" />
                <span className="text-lg font-semibold opacity-70">{product.subject}</span>
              </div>
            )}

            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              {product.is_featured && (
                <Badge className="bg-accent text-white border-0 shadow-lg">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Popular
                </Badge>
              )}
              {product.is_bundle && (
                <Badge className="bg-navy-900 text-white border-0">
                  Bundle
                </Badge>
              )}
              {discount && (
                <Badge className="bg-green-500 text-white border-0">
                  -{discount}% OFF
                </Badge>
              )}
            </div>

            {/* Share Button */}
            <button
              onClick={toggleShare}
              className={`absolute top-4 right-4 z-20 w-9 h-9 rounded-full flex items-center justify-center transition-all shadow-md ${
                shareOpen
                  ? "bg-[#e0482d] text-white"
                  : "bg-white/90 backdrop-blur-sm text-gray-600 hover:bg-white hover:text-[#e0482d]"
              }`}
              data-testid={`share-btn-${product.slug}`}
              title="Share this product"
            >
              {shareOpen ? <X className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
            </button>

            {/* Share Menu */}
            <AnimatePresence>
              {shareOpen && <ShareMenu product={product} onClose={() => setShareOpen(false)} />}
            </AnimatePresence>

            {/* Hover Action Buttons */}
            <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-3 group-hover:translate-y-0 space-y-2">
              <Button
                className="w-full bg-accent hover:bg-accent/90 text-white rounded-xl font-semibold shadow-lg"
                onClick={handleQuickBuy}
                data-testid={`quick-buy-${product.slug}`}
              >
                <Zap className="w-4 h-4 mr-2" />
                Quick Buy
              </Button>
              <Button
                className="w-full bg-white/95 backdrop-blur-sm text-navy-900 hover:bg-white rounded-xl font-semibold shadow-lg"
                onClick={handleAddToCart}
                disabled={isInCart}
                data-testid={`add-to-cart-${product.slug}`}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {isInCart ? "In Cart" : "Add to Cart"}
              </Button>
            </div>
          </div>

          {/* Product Info */}
          <div className="p-5">
            {/* Category */}
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
              <span>{product.subject}</span>
              <span>·</span>
              <span>{product.class_level}</span>
            </div>

            {/* Title */}
            <h3 className="font-bold text-navy-900 mb-2 line-clamp-2 group-hover:text-accent transition-colors duration-300">
              {product.title}
            </h3>

            {/* Meta */}
            {product.question_count && (
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                <span className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  {product.page_count || 12} pages
                </span>
                <span>{product.question_count} questions</span>
              </div>
            )}

            {/* Rating */}
            {rating && rating.count > 0 && (
              <div className="flex items-center gap-1.5 mb-3" data-testid="product-card-rating">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(rating.average) ? "fill-accent text-accent" : "text-gray-300"}`} />
                  ))}
                </div>
                <span className="text-xs text-gray-500">{rating.average}</span>
                <span className="text-xs text-gray-400">({rating.count})</span>
              </div>
            )}

            {/* Price */}
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-accent">
                {formatPrice(product.price_ghs)}
              </span>
              {product.compare_at_price_ghs && product.compare_at_price_ghs > product.price_ghs && (
                <span className="text-sm text-gray-400 line-through">
                  {formatPrice(product.compare_at_price_ghs)}
                </span>
              )}
            </div>
          </div>
        </motion.div>
      </Link>

      <QuickBuyModal
        product={product}
        open={quickBuyOpen}
        onOpenChange={setQuickBuyOpen}
      />
    </>
  );
}
